import { useContext, useEffect, useState } from 'react';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import StatsCard from '../../../components/Cards/StartsCard';
import CollapsibleContainer from '../../../components/Collapsible/CollapsibleContainer';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import Page from '../../../components/Page/Page';
import Table, { TableLabel, TableLabelBox } from '../../../components/Table/Table';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableMoneyFormatter from '../../../components/Table/TableAccessories/TableMoneyFormatter';
import { TableContext } from '../../../components/Table/TableContext';
import useRiskScan from '../../../hooks/dashboard/risk/useRiskScan';
import useRiskSummary from '../../../hooks/dashboard/risk/useRiskSummary';
import {
  ALERT_STATUS_FILTERS,
  RISK_RULES,
  RiskAlert,
  RiskWindow,
  SEVERITY_OPTIONS,
} from '../../../models/risk/risk.model';
import { capitalize, formatNaira, formatNumber } from '../../../utils/utils';
import AlertDetailSlider from './AlertDetailSlider';
import CustomerProfileSlider from './CustomerProfileSlider';
import RuleSettingsPanel from './RuleSettingsPanel';

const REFRESH_MS = 60_000;
const WINDOWS: { value: RiskWindow; label: string }[] = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

const timeAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  return minutes < 1 ? 'just now' : minutes < 60 ? `${minutes}m ago` : `${Math.floor(minutes / 60)}h ago`;
};

const RiskCenter = () => {
  // Only for the Rule performance stats: the tiles count open alerts whatever
  // the window, and the Alerts table has its own "Raised in" filter.
  const [ruleWindow, setRuleWindow] = useState<RiskWindow>('7d');
  // Auto-refresh only re-polls the summary tiles; remounting the tables on a
  // timer would close an open slider and drop the analyst's filters.
  const [tick, setTick] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const { summary, fetchSummary } = useRiskSummary();
  const { loading: scanning, scan } = useRiskScan();

  useEffect(() => {
    fetchSummary(ruleWindow);
    // eslint-disable-next-line
  }, [ruleWindow, tick, reloadKey]);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const reloadAll = () => setReloadKey((k) => k + 1);
  const severity = summary?.by_severity;

  return (
    <Page title='Hethera | Risk Center'>
      <PageLayout>
        <Container>
          <PageHeader
            title='Risk Center'
            className='borderless no-bottom-padding'
            customComp={
              <HeaderControls>
                <UpdatedText>
                  {summary
                    ? `Last scan ${timeAgo(summary.last_scan_at)}` +
                      (summary.last_scan_ms != null ? ` (${summary.last_scan_ms}ms)` : '') +
                      ' · runs every 5 min'
                    : 'Loading…'}
                </UpdatedText>
                <PrimaryBlackButton
                  type='button'
                  className='large'
                  disabled={scanning}
                  onClick={() => scan(reloadAll)}
                >
                  {scanning ? 'Scanning…' : 'Scan now'}
                </PrimaryBlackButton>
              </HeaderControls>
            }
          />

          <StatsGrid>
            <StatsCard.Root>
              <StatsCard.Header>Critical alerts</StatsCard.Header>
              <StatsCard.Value>
                <CriticalValue className={severity?.CRITICAL ? 'hot' : ''}>
                  {formatNumber(severity?.CRITICAL ?? 0)}
                </CriticalValue>
              </StatsCard.Value>
              <StatsCard.PlainFooter>Act on these now</StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>High alerts</StatsCard.Header>
              <StatsCard.Value>{formatNumber(severity?.HIGH ?? 0)}</StatsCard.Value>
              <StatsCard.PlainFooter>
                {formatNumber(severity?.MEDIUM ?? 0)} medium · {formatNumber(severity?.LOW ?? 0)}{' '}
                low
              </StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>Amount at risk</StatsCard.Header>
              <StatsCard.Value>{formatNaira(Number(summary?.amount_at_risk ?? 0))}</StatsCard.Value>
              <StatsCard.PlainFooter>Across alerts needing attention</StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>Flagged customers</StatsCard.Header>
              <StatsCard.Value>{formatNumber(summary?.flagged_customers ?? 0)}</StatsCard.Value>
              <StatsCard.PlainFooter>See the watchlist below</StatsCard.PlainFooter>
            </StatsCard.Root>
          </StatsGrid>

          <Card>
            <TableLabelBox>
              <TableLabel>Alerts</TableLabel>
            </TableLabelBox>
            <Hint>
              “Needs attention” shows every open or acknowledged alert, however old. The other
              statuses show alerts raised in the period you pick under “Raised in”.
            </Hint>
            <Table
              reloadSignal={reloadKey}
              tableHeader={{
                Severity: 'severity',
                Status: 'status',
                Alert: 'title',
                Detail: 'detail',
                Customer: 'chat_id',
                'At risk': 'amount_at_risk',
                Began: 'first_detected_at',
              }}
              tableDataSourceUrl='api/v1/admin/risk/alerts'
              size={20}
              tableFilterBoxFeatures={{
                showSearchBox: true,
                searchBoxPlaceholder: 'Search customer, reference, recipient',
                rightAttr: [{ type: 'custom-filter', comp: <AlertFiltersBar /> }],
              }}
              cellsCustomComp={{
                severity: <StatusCell statusKey='severity' />,
                status: <StatusCell statusKey='status' />,
                chat_id: <FallbackCell dataKey='chat_id' />,
                amount_at_risk: <TableMoneyFormatter dataKey='amount_at_risk' />,
                first_detected_at: <TableDateTimeFormatter dataKey='first_detected_at' />,
              }}
              clickRowShowSliderComp={(row) => (
                // After a review, refresh the tiles and reload the tables in
                // place (reloadSignal), so the open slider stays open.
                <AlertDetailSlider data={row as RiskAlert} onChanged={reloadAll} />
              )}
              hasClickableRow
              emptyStateMsg='No alerts here. Nothing needs attention'
            />
          </Card>

          <Card>
            <CardHeader>
              <TableLabelBox>
                <TableLabel>Rule performance</TableLabel>
              </TableLabelBox>
              <WindowSwitch role='group' aria-label='Stats window'>
                {WINDOWS.map((option) => (
                  <button
                    key={option.value}
                    type='button'
                    className={option.value === ruleWindow ? 'active' : ''}
                    aria-pressed={option.value === ruleWindow}
                    onClick={() => setRuleWindow(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </WindowSwitch>
            </CardHeader>
            <Hint>
              False-positive rate = dismissed ÷ (resolved + dismissed) for alerts raised in the
              window. A high rate means the rule is too sensitive; raise its threshold in Rule
              settings.
            </Hint>
            <Table
              tableHeader={{
                Rule: 'title',
                'Needs attention': 'active',
                Raised: 'raised',
                Resolved: 'resolved',
                Dismissed: 'dismissed',
                'False-positive rate': 'false_positive_rate',
              }}
              dummyData={summary?.by_rule ?? []}
              showPaginationControl={false}
              cellsCustomComp={{ false_positive_rate: <RateCell dataKey='false_positive_rate' /> }}
              emptyStateMsg='No alerts raised yet'
            />
          </Card>

          <Card>
            <TableLabelBox>
              <TableLabel>Customer watchlist</TableLabel>
            </TableLabelBox>
            <Table
              reloadSignal={reloadKey}
              tableHeader={{
                Customer: 'chat_id',
                Name: 'display_name',
                'Risk score': 'risk_score',
                Highest: 'highest_severity',
                Alerts: 'alert_count',
                'At risk': 'amount_at_risk',
              }}
              tableDataSourceUrl='api/v1/admin/risk/watchlist'
              size={10}
              cellsCustomComp={{
                display_name: <FallbackCell dataKey='display_name' />,
                highest_severity: <StatusCell statusKey='highest_severity' />,
                amount_at_risk: <TableMoneyFormatter dataKey='amount_at_risk' />,
              }}
              clickRowShowSliderComp={(row) => <CustomerProfileSlider userId={row.user_id} />}
              hasClickableRow
              emptyStateMsg='No customers flagged'
            />
          </Card>

          <Card>
            <CollapsibleContainer title={<TableLabel>Rule settings</TableLabel>}>
              <RuleSettingsPanel onSaved={() => scan(reloadAll)} />
            </CollapsibleContainer>
          </Card>
        </Container>
      </PageLayout>
    </Page>
  );
};

export default RiskCenter;

const AlertFiltersBar = () => {
  const tableContext = useContext(TableContext);
  const setFilter = (key: string, value: string) =>
    tableContext?.handleSetTableFilterRecord({ [key]: value, page: 0 } as any);

  return (
    <FiltersRow>
      <Select
        defaultValue='ACTIVE'
        onChange={(e) => setFilter('status', e.target.value)}
        aria-label='Filter by status'
      >
        {ALERT_STATUS_FILTERS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <Select
        defaultValue='7d'
        onChange={(e) => setFilter('window', e.target.value)}
        aria-label='Raised in'
        title='Raised in (for statuses other than Needs attention)'
      >
        {WINDOWS.map((option) => (
          <option key={option.value} value={option.value}>
            Raised in the last {option.label}
          </option>
        ))}
      </Select>
      <Select
        defaultValue=''
        onChange={(e) => setFilter('severity', e.target.value)}
        aria-label='Filter by severity'
      >
        <option value=''>All severities</option>
        {SEVERITY_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {capitalize(option)}
          </option>
        ))}
      </Select>
      <Select
        defaultValue=''
        onChange={(e) => setFilter('rule', e.target.value)}
        aria-label='Filter by rule'
      >
        <option value=''>All rules</option>
        {Object.entries(RISK_RULES).map(([rule, title]) => (
          <option key={rule} value={rule}>
            {title}
          </option>
        ))}
      </Select>
    </FiltersRow>
  );
};

interface CellProps {
  data?: Record<string, any>;
  dataKey?: string;
}

const FallbackCell: React.FC<CellProps> = ({ data, dataKey }) => (
  <>{(data && dataKey && data[dataKey]) || '—'}</>
);

const RateCell: React.FC<CellProps> = ({ data, dataKey }) => {
  const rate = data && dataKey ? data[dataKey] : null;
  if (rate === null || rate === undefined) return <>Not reviewed yet</>;
  return <Rate className={rate >= 50 ? 'hot' : ''}>{Number(rate).toFixed(0)}%</Rate>;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const UpdatedText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};
  margin: 8px 0 12px;
`;

const WindowSwitch = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  overflow: hidden;

  & > button {
    border: none;
    background: white;
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.secondaryTextColor};
  }

  & > button.active {
    background: ${({ theme }) => theme.palette.HetheraBlack};
    color: white;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const CriticalValue = styled.span`
  &.hot {
    color: ${({ theme }) => theme.palette.errorRed};
  }
`;

const Rate = styled.span`
  &.hot {
    color: ${({ theme }) => theme.palette.errorRed};
    font-weight: 600;
  }
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: var(--radius);
  padding: 24px;
  background-color: white;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.HetheraBlack};
  background: white;
`;
